import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Clock,
  FileText,
  Filter,
  Gauge,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Sheet,
  Trash2,
  WalletCards,
  X,
} from 'lucide-react';
import { Header } from '../components/Layout/Header';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { Pagination } from '../components/common/Pagination';
import { ProjectForm } from '../components/Projects/ProjectForm';
import { mileageApi, projectsApi } from '../services/api';
import { MileageRecord, MileageStatus, MileageVehicle, Project } from '../types';
import { formatBRLCurrencyInput, parseBRLCurrency } from '../utils/currency';
import { useAuthStore } from '../store/authStore';
import { useProjectStore } from '../store/projectStore';

const today = new Date().toISOString().slice(0, 10);
const monthStart = `${today.slice(0, 8)}01`;
const defaultRateInput = formatBRLCurrencyInput('0.00');
const DEFAULT_PAGE_SIZE = 10;

interface MileageFormData {
  project_id: string;
  vehicle_id: string;
  vehicle_name: string;
  vehicle_plate: string;
  driver_name: string;
  started_at: string;
  origin: string;
  destination: string;
  km_start: string;
  km_end: string;
  rate_per_km: string;
  purpose: string;
  notes: string;
}

function emptyMileageForm(projectId = '', vehicleId = ''): MileageFormData {
  return {
    project_id: projectId,
    vehicle_id: vehicleId,
    vehicle_name: '',
    vehicle_plate: '',
    driver_name: '',
    started_at: today,
    origin: '',
    destination: '',
    km_start: '',
    km_end: '',
    rate_per_km: defaultRateInput,
    purpose: '',
    notes: '',
  };
}

function toNumber(value: number | string | undefined | null): number {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function parseDecimal(value: string): number {
  // Km inputs hold either what the user typed (plain digits, optionally with a
  // comma decimal) or the raw API value (e.g. "1000.0", dot as decimal) when
  // populating the edit form — never a thousands separator, so '.' must stay
  // a decimal point rather than being stripped.
  const normalized = value.trim().replace(/[^\d,.-]/g, '').replace(',', '.');
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatKm(value: number | string | undefined | null): string {
  return `${toNumber(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

function formatCurrency(value: number | string | undefined | null): string {
  return toNumber(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dateOnly(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function displayDate(value?: string | null): string {
  if (!value) return '-';
  const [year, month, day] = dateOnly(value).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function projectLabel(record: Pick<MileageRecord, 'management_code' | 'project_name'>): string {
  return record.management_code ? `${record.management_code} - ${record.project_name || ''}` : record.project_name || '-';
}

function projectOptionLabel(project: Pick<Project, 'management_code' | 'name'>): string {
  return project.management_code ? `${project.management_code} - ${project.name}` : project.name;
}

function vehicleRecordKey(record: Pick<MileageRecord, 'vehicle_id' | 'vehicle_name' | 'vehicle_plate'>): string {
  return record.vehicle_id || `${record.vehicle_name}-${record.vehicle_plate || ''}`;
}

interface VehicleTotal extends MileageVehicle {
  count: number;
  distance: number;
  amount: number;
}

function fileDate(value: string): string {
  return value ? value.replace(/-/g, '') : 'sem-data';
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function tableRows(rows: string[][]): string {
  return rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('');
}

function tableHead(columns: string[]): string {
  return `<thead><tr>${columns.map(column => `<th>${escapeHtml(column)}</th>`).join('')}</tr></thead>`;
}

function buildMileageReportHtml({
  records,
  vehicleTotals,
  totals,
  filters,
  generatedAt,
}: {
  records: MileageRecord[];
  vehicleTotals: VehicleTotal[];
  totals: { totalKm: number; totalAmount: number; recordCount: number };
  filters: { project: string; vehicle: string; startDate: string; endDate: string };
  generatedAt: string;
}) {
  const period = `${displayDate(filters.startDate)} a ${displayDate(filters.endDate)}`;

  const summaryRows = tableRows([
    ['Km no período', escapeHtml(formatKm(totals.totalKm))],
    ['Valor total', escapeHtml(formatCurrency(totals.totalAmount))],
    ['Lançamentos', String(totals.recordCount)],
  ]);

  const recordRows = records.length > 0
    ? tableRows(records.map(record => [
      escapeHtml(displayDate(record.started_at)),
      escapeHtml(record.driver_name),
      escapeHtml(`${record.vehicle_name} ${record.vehicle_plate || ''}`.trim()),
      escapeHtml(projectLabel(record)),
      escapeHtml(record.origin),
      escapeHtml(record.destination),
      escapeHtml(formatKm(record.distance_km)),
      escapeHtml(formatCurrency(record.rate_per_km)),
      escapeHtml(formatCurrency(record.amount)),
      escapeHtml(record.purpose || ''),
    ]))
    : '<tr><td colspan="10">Nenhum lançamento encontrado para os filtros atuais.</td></tr>';

  const vehicleRows = vehicleTotals.length > 0
    ? tableRows(vehicleTotals.map(vehicle => [
      escapeHtml(`${vehicle.name} ${vehicle.plate || ''}`.trim()),
      String(vehicle.count),
      escapeHtml(formatKm(vehicle.distance)),
      escapeHtml(formatCurrency(vehicle.amount)),
    ]))
    : '<tr><td colspan="4">Nenhum uso de veículo no período.</td></tr>';

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Relatorio de quilometragem</title>
  <style>
    body { color: #323338; font-family: Arial, sans-serif; margin: 24px; }
    h1 { font-size: 22px; margin: 0 0 8px; }
    h2 { border-bottom: 1px solid #d0d4e4; font-size: 15px; margin: 24px 0 8px; padding-bottom: 6px; }
    p { color: #676879; font-size: 12px; margin: 2px 0; }
    table { border-collapse: collapse; font-size: 11px; margin-top: 8px; width: 100%; }
    th { background: #f7f9ff; color: #323338; font-weight: 700; text-align: left; }
    th, td { border: 1px solid #d0d4e4; padding: 6px; vertical-align: top; }
    @media print {
      body { margin: 12mm; }
      h2 { break-after: avoid; page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    }
  </style>
</head>
<body>
  <h1>Relatório de quilometragem</h1>
  <p>Projeto: ${escapeHtml(filters.project)} | Veículo: ${escapeHtml(filters.vehicle)}</p>
  <p>Período: ${escapeHtml(period)}</p>
  <p>Gerado em: ${escapeHtml(generatedAt)}</p>

  <h2>Resumo</h2>
  <table>
    ${tableHead(['Indicador', 'Valor'])}
    <tbody>${summaryRows}</tbody>
  </table>

  <h2>Lançamentos</h2>
  <table>
    ${tableHead(['Data', 'Motorista', 'Veículo', 'Projeto', 'Origem', 'Destino', 'Distância', 'Valor/km', 'Valor', 'Finalidade'])}
    <tbody>${recordRows}</tbody>
  </table>

  <h2>Por veículo</h2>
  <table>
    ${tableHead(['Veículo', 'Lançamentos', 'Distância', 'Valor'])}
    <tbody>${vehicleRows}</tbody>
  </table>
</body>
</html>`;
}

function mileageToForm(record: MileageRecord): MileageFormData {
  return {
    project_id: record.project_id,
    vehicle_id: record.vehicle_id || '',
    vehicle_name: record.vehicle_id ? '' : record.vehicle_name,
    vehicle_plate: record.vehicle_id ? '' : record.vehicle_plate || '',
    driver_name: record.driver_name,
    started_at: dateOnly(record.started_at),
    origin: record.origin,
    destination: record.destination,
    km_start: String(record.km_start),
    km_end: String(record.km_end),
    rate_per_km: formatBRLCurrencyInput(toNumber(record.rate_per_km).toFixed(2)),
    purpose: record.purpose || '',
    notes: record.notes || '',
  };
}

export function MileagePage() {
  const createProject = useProjectStore(state => state.createProject);
  const { user: currentUser } = useAuthStore();
  const canManageMileage = currentUser?.role === 'admin' || currentUser?.role === 'finance' || currentUser?.role === 'super_admin';

  const [projects, setProjects] = useState<Project[]>([]);
  const [vehicles, setVehicles] = useState<MileageVehicle[]>([]);
  const [records, setRecords] = useState<MileageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMileageModalOpen, setIsMileageModalOpen] = useState(false);
  const [viewingRecord, setViewingRecord] = useState<MileageRecord | null>(null);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [mileageForm, setMileageForm] = useState<MileageFormData>(emptyMileageForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(today);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  const calculatedDistance = Math.max(parseDecimal(mileageForm.km_end) - parseDecimal(mileageForm.km_start), 0);
  const calculatedRate = parseBRLCurrency(mileageForm.rate_per_km) || 0;
  const calculatedAmount = calculatedDistance * calculatedRate;

  const availableVehicles = useMemo(() => {
    if (vehicles.length > 0) return vehicles;

    const distinct = new Map<string, MileageVehicle>();
    records.forEach(record => {
      const key = vehicleRecordKey(record);
      if (!distinct.has(key)) {
        distinct.set(key, {
          id: record.vehicle_id || key,
          name: record.vehicle_name,
          plate: record.vehicle_plate || '',
          fuel_type: null,
          is_active: true,
        });
      }
    });
    return Array.from(distinct.values());
  }, [records, vehicles]);

  const loadMileageData = useCallback(() => {
    setLoading(true);
    Promise.all([
      projectsApi.getAllForSelector(),
      mileageApi.getVehicles(),
      mileageApi.getAll(),
    ])
      .then(([projectsResponse, vehiclesResponse, recordsResponse]) => {
        setProjects(projectsResponse.data);
        setVehicles(vehiclesResponse.data);
        setRecords(recordsResponse.data);
      })
      .catch(() => {
        toast.error('Falha ao carregar quilometragem');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadMileageData();
  }, [loadMileageData]);

  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return records.filter(record => {
      if (projectId && record.project_id !== projectId) return false;
      if (vehicleId && vehicleRecordKey(record) !== vehicleId) return false;
      if (startDate && dateOnly(record.started_at) < startDate) return false;
      if (endDate && dateOnly(record.started_at) > endDate) return false;
      if (!q) return true;

      const haystack = [
        record.origin,
        record.destination,
        record.vehicle_name,
        record.vehicle_plate || '',
        record.driver_name,
        record.project_name || '',
        record.management_code || '',
        record.purpose || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [endDate, projectId, records, searchQuery, startDate, vehicleId]);

  const pagedRecords = useMemo(
    () => filteredRecords.slice((page - 1) * pageSize, page * pageSize),
    [filteredRecords, page, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [filteredRecords]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const handleCreateProject = async (data: Partial<Project>, addedMemberIds?: string[]) => {
    try {
      const newProject = await createProject(data, addedMemberIds);
      setProjects(prev => [newProject, ...prev]);
      setIsCreateProjectModalOpen(false);
      toast.success('Projeto criado!');
    } catch {
      toast.error('Falha ao criar projeto');
    }
  };

  const totalKm = filteredRecords.reduce((sum, record) => sum + toNumber(record.distance_km), 0);
  const totalAmount = filteredRecords.reduce((sum, record) => sum + toNumber(record.amount), 0);
  const recordCount = filteredRecords.length;

  const vehicleTotals = useMemo(() => availableVehicles.map(vehicle => {
    const vehicleRecords = filteredRecords.filter(record => vehicleRecordKey(record) === vehicle.id);
    const distance = vehicleRecords.reduce((sum, record) => sum + toNumber(record.distance_km), 0);
    const amount = vehicleRecords.reduce((sum, record) => sum + toNumber(record.amount), 0);
    return { ...vehicle, count: vehicleRecords.length, distance, amount };
  }).filter(vehicle => vehicle.count > 0), [availableVehicles, filteredRecords]);

  const reportProjectLabel = useMemo(() => {
    const project = projects.find(item => item.id === projectId);
    return project ? projectOptionLabel(project) : 'Todos os projetos';
  }, [projectId, projects]);

  const reportVehicleLabel = useMemo(() => {
    const vehicle = availableVehicles.find(item => item.id === vehicleId);
    return vehicle ? `${vehicle.name} ${vehicle.plate || ''}`.trim() : 'Todos os veículos';
  }, [availableVehicles, vehicleId]);

  const buildReportHtml = useCallback(() => buildMileageReportHtml({
    records: filteredRecords,
    vehicleTotals,
    totals: { totalKm, totalAmount, recordCount },
    filters: { project: reportProjectLabel, vehicle: reportVehicleLabel, startDate, endDate },
    generatedAt: new Date().toLocaleString('pt-BR'),
  }), [endDate, filteredRecords, reportProjectLabel, reportVehicleLabel, recordCount, startDate, totalAmount, totalKm, vehicleTotals]);

  const handleExportPdf = useCallback(() => {
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      toast.error('Permita pop-ups para gerar o PDF');
      return;
    }

    reportWindow.document.open();
    reportWindow.document.write(buildReportHtml());
    reportWindow.document.close();
    reportWindow.focus();
    window.setTimeout(() => {
      reportWindow.print();
    }, 250);
  }, [buildReportHtml]);

  const handleExportExcel = useCallback(() => {
    const blob = new Blob([`\ufeff${buildReportHtml()}`], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-quilometragem-${fileDate(startDate)}-${fileDate(endDate)}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Relatório Excel gerado');
  }, [buildReportHtml, endDate, startDate]);

  const openMileageModal = () => {
    const initialProjectId = projects[0]?.id || '';
    const initialVehicleId = vehicles[0]?.id || '';
    setEditingRecordId(null);
    setMileageForm(emptyMileageForm(initialProjectId, initialVehicleId));
    setIsMileageModalOpen(true);
  };

  const openViewModal = (record: MileageRecord) => {
    setViewingRecord(record);
  };

  const openEditModal = (record: MileageRecord) => {
    if (record.status !== 'draft' && !canManageMileage) {
      toast.error('Apenas lançamentos em aberto podem ser editados');
      return;
    }
    setEditingRecordId(record.id);
    setMileageForm(mileageToForm(record));
    setIsMileageModalOpen(true);
  };

  const handleSubmitMileage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mileageForm.project_id) {
      toast.error('Selecione um projeto');
      return;
    }
    if (!mileageForm.vehicle_id && !mileageForm.vehicle_name.trim()) {
      toast.error('Informe o veículo');
      return;
    }
    if (!mileageForm.driver_name.trim()) {
      toast.error('Informe o motorista');
      return;
    }
    if (!calculatedDistance) {
      toast.error('Informe uma quilometragem final maior que a inicial');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_id: mileageForm.project_id,
        vehicle_id: mileageForm.vehicle_id || undefined,
        vehicle_name: mileageForm.vehicle_id ? undefined : mileageForm.vehicle_name.trim(),
        vehicle_plate: mileageForm.vehicle_id ? undefined : mileageForm.vehicle_plate.trim() || undefined,
        driver_name: mileageForm.driver_name.trim(),
        started_at: mileageForm.started_at,
        origin: mileageForm.origin.trim(),
        destination: mileageForm.destination.trim(),
        km_start: parseDecimal(mileageForm.km_start),
        km_end: parseDecimal(mileageForm.km_end),
        rate_per_km: calculatedRate,
        purpose: mileageForm.purpose.trim() || undefined,
        notes: mileageForm.notes.trim() || undefined,
        // Only set on create: this form has no status control, so on edit
        // omitting it (rather than forcing 'draft') keeps whatever status a
        // manager may have moved the record to (e.g. 'paid').
        status: editingRecordId ? undefined : ('draft' as MileageStatus),
      };

      if (editingRecordId) {
        const response = await mileageApi.update(editingRecordId, payload);
        setRecords(prev => prev.map(record => (record.id === editingRecordId ? response.data : record)));
        toast.success('Lançamento atualizado');
      } else {
        const response = await mileageApi.create(payload);
        setRecords(prev => [response.data, ...prev]);
        toast.success('Lançamento de quilometragem salvo');
      }

      setIsMileageModalOpen(false);
      setEditingRecordId(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Falha ao salvar quilometragem');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record: MileageRecord) => {
    const confirmed = window.confirm(`Excluir o lançamento de ${displayDate(record.started_at)} entre ${record.origin} e ${record.destination}?`);
    if (!confirmed) return;

    setDeletingId(record.id);
    try {
      await mileageApi.delete(record.id);
      setRecords(prev => prev.filter(item => item.id !== record.id));
      toast.success('Lançamento excluído');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Falha ao excluir lançamento');
    } finally {
      setDeletingId(null);
    }
  };

  const useVehicleSelect = vehicles.length > 0 && (!!mileageForm.vehicle_id || !mileageForm.vehicle_name);

  return (
    <div>
      <Header title="Quilometragem" />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-4">
            <div className="text-sm font-medium text-[#323338]">
              <label htmlFor="mileage-project-filter">Projeto</label>
              <div className="mt-1 flex items-center gap-2">
                <select id="mileage-project-filter" value={projectId} onChange={event => setProjectId(event.target.value)} className="h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none">
                  <option value="">Todos os projetos</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{projectOptionLabel(project)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsCreateProjectModalOpen(true)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#C24914] text-white transition-colors hover:bg-[#A83D10]"
                  title="Adicionar novo projeto"
                  aria-label="Adicionar novo projeto"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
            <label className="text-sm font-medium text-[#323338]">
              Veículo
              <select value={vehicleId} onChange={event => setVehicleId(event.target.value)} className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none">
                <option value="">Todos</option>
                {availableVehicles.map(vehicle => <option key={vehicle.id} value={vehicle.id}>{vehicle.name} {vehicle.plate}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-[#323338]">
              Início
              <input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none" />
            </label>
            <label className="text-sm font-medium text-[#323338]">
              Fim
              <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none" />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" icon={<Plus size={16} />} onClick={openMileageModal} disabled={projects.length === 0}>
              Novo lançamento
            </Button>
          </div>
        </div>

        <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded border border-[#D0D4E4] bg-white px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-[#323338]">Relatórios de quilometragem</h2>
            <p className="mt-1 text-xs text-[#676879]">Gere o relatório com os filtros atuais de projeto, veículo e período.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" icon={<FileText size={16} />} onClick={handleExportPdf} disabled={loading}>
              Gerar PDF
            </Button>
            <Button type="button" variant="secondary" icon={<Sheet size={16} />} onClick={handleExportExcel} disabled={loading}>
              Exportar Excel
            </Button>
          </div>
        </section>

        <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Metric title="Km no período" value={formatKm(totalKm)} note={`${recordCount} deslocamento(s)`} icon={<Gauge size={18} />} />
          <Metric title="Valor total" value={formatCurrency(totalAmount)} note="Estimado no período" icon={<WalletCards size={18} />} />
          <Metric title="Lançamentos" value={recordCount.toString()} note="No período selecionado" icon={<Clock size={18} />} />
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
          <section className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center justify-between border-b border-[#D0D4E4] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#323338]">Lançamentos</h2>
              <span className="text-xs text-[#676879]">{filteredRecords.length} de {records.length} registro(s)</span>
            </div>
            <div className="border-b border-[#D0D4E4] bg-[#F7F9FF] px-4 py-3">
              <div className="relative">
                <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C24914]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Buscar por rota, veículo, projeto ou finalidade"
                  className="h-11 w-full rounded-lg border-2 border-[#edc6b5] bg-white pl-10 pr-10 text-sm font-medium text-[#323338] shadow-sm placeholder:font-normal placeholder:text-[#8C91A3] focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#edc6b5]"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C91A3] transition-colors hover:text-[#323338]" title="Limpar busca">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C24914] border-t-transparent" />
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#676879]">Nenhuma quilometragem encontrada para os filtros atuais.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#D0D4E4] text-sm">
                  <thead className="bg-[#F7F9FF] text-left text-xs font-semibold uppercase text-[#676879]">
                    <tr>
                      <th className="px-4 py-3">Data</th>
                      <th className="px-4 py-3">Trajeto</th>
                      <th className="px-4 py-3">Projeto</th>
                      <th className="px-4 py-3">Veículo</th>
                      <th className="px-4 py-3">Motorista</th>
                      <th className="px-4 py-3 text-right">Km</th>
                      <th className="px-4 py-3 text-right">Valor</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EEF1F7]">
                    {pagedRecords.map(record => (
                      <tr key={record.id} onClick={() => openViewModal(record)} className="cursor-pointer hover:bg-[#F7F9FF]">
                        <td className="whitespace-nowrap px-4 py-3 text-[#323338]">{displayDate(record.started_at)}</td>
                        <td className="min-w-[260px] px-4 py-3">
                          <p className="font-semibold text-[#323338]">{record.origin} → {record.destination}</p>
                          <p className="mt-0.5 text-xs text-[#676879]">Km inicial {record.km_start} | final {record.km_end}</p>
                          {record.purpose && <p className="mt-1 text-xs text-[#C24914]">{record.purpose}</p>}
                        </td>
                        <td className="px-4 py-3 text-[#323338]">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: record.project_color || '#C24914' }} />
                            <span>{projectLabel(record)}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-[#676879]">{record.vehicle_name} {record.vehicle_plate}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-[#676879]">{record.driver_name}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#323338]">{formatKm(record.distance_km)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#323338]">{formatCurrency(record.amount)}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={event => { event.stopPropagation(); openEditModal(record); }} className="rounded p-1.5 text-[#676879] transition-colors hover:bg-[#f9f3f0] hover:text-[#C24914]" title="Editar lançamento">
                              <Pencil size={14} />
                            </button>
                            <button type="button" onClick={event => event.stopPropagation()} className="rounded p-1.5 text-[#676879] transition-colors hover:bg-[#f9f3f0] hover:text-[#C24914]" title="Anexos">
                              <Paperclip size={14} />
                            </button>
                            {canManageMileage && (
                              <button type="button" disabled={deletingId === record.id} onClick={event => { event.stopPropagation(); handleDelete(record); }} className="rounded p-1.5 text-[#676879] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50" title="Excluir lançamento">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={page} pageSize={pageSize} totalItems={filteredRecords.length} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />
          </section>

          <aside className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center justify-between border-b border-[#D0D4E4] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#323338]">Por veículo</h2>
              <Filter size={15} className="text-[#676879]" />
            </div>
            <div className="divide-y divide-[#EEF1F7]">
              {vehicleTotals.map(vehicle => {
                const percent = totalKm > 0 ? Math.min(100, Math.round((vehicle.distance / totalKm) * 100)) : 0;
                return (
                  <div key={vehicle.id} className="px-4 py-3">
                    <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-[#323338]">{vehicle.name} {vehicle.plate}</span>
                      <span className="text-xs text-[#676879]">{formatKm(vehicle.distance)}</span>
                    </div>
                    <p className="mb-2 text-xs text-[#676879]">{vehicle.count} lançamento(s) | {formatCurrency(vehicle.amount)}</p>
                    <div className="h-2 overflow-hidden rounded-full bg-[#EEF1F7]">
                      <div className="h-full rounded-full bg-[#C24914]" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
              {vehicleTotals.length === 0 && !loading && <div className="px-4 py-10 text-center text-sm text-[#676879]">Nenhum uso de veículo no período.</div>}
            </div>
          </aside>
        </div>
      </div>

      <Modal isOpen={isMileageModalOpen} onClose={() => setIsMileageModalOpen(false)} title={editingRecordId ? 'Editar lançamento de quilometragem' : 'Novo lançamento de quilometragem'} size="xl">
        <form onSubmit={handleSubmitMileage} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Projeto *">
              <select required value={mileageForm.project_id} onChange={event => setMileageForm(prev => ({ ...prev, project_id: event.target.value }))} className="input-field">
                {projects.map(project => <option key={project.id} value={project.id}>{projectOptionLabel(project)}</option>)}
              </select>
            </Field>
            {useVehicleSelect ? (
              <Field label="Veículo *">
                <select required value={mileageForm.vehicle_id} onChange={event => setMileageForm(prev => ({ ...prev, vehicle_id: event.target.value, vehicle_name: '', vehicle_plate: '' }))} className="input-field">
                  {vehicles.map(vehicle => <option key={vehicle.id} value={vehicle.id}>{vehicle.name} {vehicle.plate}</option>)}
                </select>
              </Field>
            ) : (
              <Field label="Veículo *">
                <input required value={mileageForm.vehicle_name} onChange={event => setMileageForm(prev => ({ ...prev, vehicle_id: '', vehicle_name: event.target.value }))} className="input-field" placeholder="Ex.: Hilux" />
              </Field>
            )}
            <Field label="Motorista *">
              <input required value={mileageForm.driver_name} onChange={event => setMileageForm(prev => ({ ...prev, driver_name: event.target.value }))} className="input-field" placeholder="Nome do motorista" />
            </Field>
            <Field label="Data *">
              <input required type="date" value={mileageForm.started_at} onChange={event => setMileageForm(prev => ({ ...prev, started_at: event.target.value }))} className="input-field" />
            </Field>
            <Field label="Valor por km *">
              <input required value={mileageForm.rate_per_km} onChange={event => setMileageForm(prev => ({ ...prev, rate_per_km: formatBRLCurrencyInput(event.target.value) }))} className="input-field" placeholder="R$ 0,00" />
            </Field>
            {!useVehicleSelect && (
              <Field label="Placa">
                <input value={mileageForm.vehicle_plate} onChange={event => setMileageForm(prev => ({ ...prev, vehicle_plate: event.target.value }))} className="input-field" placeholder="Ex.: QAB-4D28" />
              </Field>
            )}
            <Field label="Origem *">
              <input required value={mileageForm.origin} onChange={event => setMileageForm(prev => ({ ...prev, origin: event.target.value }))} className="input-field" placeholder="Ex.: Escritório" />
            </Field>
            <Field label="Destino *">
              <input required value={mileageForm.destination} onChange={event => setMileageForm(prev => ({ ...prev, destination: event.target.value }))} className="input-field" placeholder="Ex.: Fazenda Primavera" />
            </Field>
            <Field label="Km inicial *">
              <input required value={mileageForm.km_start} onChange={event => setMileageForm(prev => ({ ...prev, km_start: event.target.value }))} className="input-field" placeholder="18240" />
            </Field>
            <Field label="Km final *">
              <input required value={mileageForm.km_end} onChange={event => setMileageForm(prev => ({ ...prev, km_end: event.target.value }))} className="input-field" placeholder="18374" />
            </Field>
          </div>
          <Field label="Finalidade">
            <input value={mileageForm.purpose} onChange={event => setMileageForm(prev => ({ ...prev, purpose: event.target.value }))} className="input-field" placeholder="Visita técnica, entrega, reunião..." />
          </Field>
          <Field label="Observações">
            <textarea value={mileageForm.notes} onChange={event => setMileageForm(prev => ({ ...prev, notes: event.target.value }))} className="input-field min-h-[78px] resize-none" />
          </Field>
          <div className="grid grid-cols-1 gap-3 rounded border border-[#D0D4E4] bg-[#F7F9FF] p-4 md:grid-cols-2">
            <Calculated label="Distância calculada" value={formatKm(calculatedDistance)} />
            <Calculated label="Valor estimado" value={formatCurrency(calculatedAmount)} />
          </div>
          <div className="rounded border border-[#D0D4E4] bg-white px-4 py-3 text-sm text-[#676879]">
            Anexos podem ser adicionados depois que o lançamento for salvo, como foto do hodômetro ou comprovante de deslocamento.
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsMileageModalOpen(false)} className="flex-1 justify-center">Cancelar</Button>
            <Button type="submit" loading={saving} className="flex-1 justify-center">Salvar lançamento</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!viewingRecord} onClose={() => setViewingRecord(null)} title="Detalhes do lançamento" size="xl">
        {viewingRecord && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <ViewField label="Projeto" value={projectLabel(viewingRecord)} />
              <ViewField label="Veículo" value={`${viewingRecord.vehicle_name} ${viewingRecord.vehicle_plate || ''}`.trim()} />
              <ViewField label="Motorista" value={viewingRecord.driver_name} />
              <ViewField label="Data" value={displayDate(viewingRecord.started_at)} />
              <ViewField label="Origem" value={viewingRecord.origin} />
              <ViewField label="Destino" value={viewingRecord.destination} />
              <ViewField label="Km inicial" value={String(viewingRecord.km_start)} />
              <ViewField label="Km final" value={String(viewingRecord.km_end)} />
            </div>
            {viewingRecord.purpose && <ViewField label="Finalidade" value={viewingRecord.purpose} />}
            {viewingRecord.notes && <ViewField label="Observações" value={viewingRecord.notes} />}
            <div className="grid grid-cols-1 gap-3 rounded border border-[#D0D4E4] bg-[#F7F9FF] p-4 md:grid-cols-3">
              <Calculated label="Distância" value={formatKm(viewingRecord.distance_km)} />
              <Calculated label="Valor por km" value={formatCurrency(viewingRecord.rate_per_km)} />
              <Calculated label="Valor total" value={formatCurrency(viewingRecord.amount)} />
            </div>
            <div className="flex justify-end pt-2">
              <Button type="button" variant="secondary" onClick={() => setViewingRecord(null)}>Fechar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={isCreateProjectModalOpen} onClose={() => setIsCreateProjectModalOpen(false)} title="Novo Projeto" size="lg">
        <ProjectForm onSubmit={handleCreateProject} onCancel={() => setIsCreateProjectModalOpen(false)} />
      </Modal>
    </div>
  );
}

function Metric({ title, value, note, icon }: { title: string; value: string; note: string; icon: React.ReactNode }) {
  return (
    <div className="rounded border border-[#D0D4E4] bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-[#676879]">{title}</p>
          <p className="mt-1 break-words text-xl font-semibold text-[#323338]">{value}</p>
          <p className="mt-1 text-xs text-[#676879]">{note}</p>
        </div>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#f9f3f0] text-[#C24914]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function ViewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-[#676879]">{label}</p>
      <p className="mt-1 text-sm text-[#323338]">{value || '-'}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Calculated({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-[#676879]">{label}</p>
      <p className="mt-1 text-lg font-semibold text-[#323338]">{value}</p>
    </div>
  );
}
